import * as Yup from 'yup';
import { AppDispatch } from '@/app/store';
import { AuthorDTO } from '@/types/AuthorDTO';
import { AuthorForm } from '@/components/create/AuthorForm';
import { CreateFormProps } from '@/types/create/CreateFormProps';
import { CreateFormValues } from '@/types/create/CreateFormValues';
import { DepartmentDTO } from '@/types/DepartmentDTO';
import { FixedButton } from '@/components/common/button/FixedButton';
import { Form, Formik, FormikProps } from 'formik';
import { Grid, Theme, Typography } from '@mui/material';
import { LetterAlert } from '@/components/create/letter/LetterAlert';
import { MobileBar } from '@/components/home/MobileBar';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { RegionDTO } from '@/types/RegionDTO';
import { SenderDTO } from '@/types/SenderDTO';
import { UserDTO } from '@/types/user/UserDTO';
import { emailSchema } from '@/validation/emailSchema';
import { formatUserFullName, removeExtraSpaces } from '@/utils/string-utils';
import { fullNameSchema } from '@/validation/fullNameSchema';
import { getAuthor, getDepartment, getRegion, getSender, setAuthor } from '@/services/createFormSlice';
import { getCurrentUser } from '@/services/authSlice';
import { phoneFieldSchema } from '@/validation/phoneSchema';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetCurrentUserQuery } from '@/services/api/authApiSlice';
import React, { FC, ReactElement, useEffect, useState } from 'react';

const validationSchema = Yup.object({
  authorFullName: fullNameSchema,
  authorPhoneNumber: phoneFieldSchema,
  authorEmail: emailSchema.oneOf([Yup.ref('authorConfirmEmail')], 'Поля не совпадают'),
  authorConfirmEmail: emailSchema.oneOf([Yup.ref('authorEmail')], 'Поля не совпадают'),
});

export const CreateFormAuthorMobile: FC<CreateFormProps> = (props: CreateFormProps): ReactElement => {
  const { nextFormPath, previousFormPath, previousFormLabel, errorRedirectPath, isMobile, mobileTittle } = props;

  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();

  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);
  const department: DepartmentDTO | null = useAppSelector(getDepartment);
  const region: RegionDTO | null = useAppSelector(getRegion);
  const sender: SenderDTO | null = useAppSelector(getSender);
  const author: AuthorDTO | null = useAppSelector(getAuthor);

  useEffect((): void => {
    if (!(department && region && sender)) {
      if (errorRedirectPath) {
        navigate(errorRedirectPath);
      }
    }
  }, [department, errorRedirectPath, navigate, region, sender]);

  const { data: user } = useGetCurrentUserQuery(authUser?.id, {
    skip: !authUser,
  });

  const [emailWarn, setEmailWarn] = useState<boolean>(false);

  const initialValues: CreateFormValues = {
    prisonerLastName: '',
    prisonerFirstName: '',
    prisonerMiddleName: '',
    prisonerBirthDate: '',
    authorFullName: author?.name || (user ? formatUserFullName(user) : ''),
    authorPhoneNumber: author?.phone || (user && user.phone ? user.phone : ''),
    authorEmail: author?.email || (user ? user.email : ''),
    authorConfirmEmail: author?.email || (user ? user.email : ''),
  };

  const handleSubmit = (values: CreateFormValues): void => {
    dispatch(
      setAuthor({
        name: removeExtraSpaces(values.authorFullName),
        phone: values.authorPhoneNumber,
        email: values.authorEmail,
      }),
    );
    if (nextFormPath) {
      navigate(nextFormPath);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      validateOnChange={true}
      onSubmit={handleSubmit}>
      {(formikProps: FormikProps<CreateFormValues>) => {
        const { submitForm, setFieldValue } = formikProps;

        const handleSetFieldValue = (field: string, value: unknown, isErrorField: boolean) => {
          setFieldValue(field, value, isErrorField);
        };

        return (
          <Form>
            <Grid
              container={true}
              direction={'column'}
              sx={{
                paddingBottom: (theme: Theme) => (isMobile ? theme.spacing(6) : theme.spacing(0)),
              }}>
              <Grid item={true} mb={5}>
                <PageHeader
                  previousFormLabel={previousFormLabel}
                  previousFormPath={previousFormPath}
                  isMobile={isMobile}
                  mobileTittle={mobileTittle}
                />
              </Grid>
              <Grid item={true}>
                <AuthorForm
                  formikProps={formikProps}
                  handleSetFieldValue={handleSetFieldValue}
                  handleValidEmail={setEmailWarn}
                  isMobile={isMobile}
                />
              </Grid>
              {emailWarn && (
                <Grid item={true} mt={3}>
                  <LetterAlert>
                    <Typography variant={'body1'}>
                      {'Введённая почта не совпадает с указанной в настройках личного кабинета.'}
                    </Typography>
                  </LetterAlert>
                </Grid>
              )}
              <Grid item={true} marginTop={'32px'}>
                <MobileBar>
                  <FixedButton color={'primary'} variant={'contained'} onClick={submitForm}>
                    {'Продолжить'}
                  </FixedButton>
                </MobileBar>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
